import React, { useState, useEffect } from 'react';
import AttributedOrders from '../components/AttributedOrders';
import VipBadge from '../components/VipBadge';
import { sessionHeaders } from '../services/auth';

const PROXY = 'https://crabplace-dotdigital.netlify.app/.netlify/functions';

function fmtPct(n) {
  if (n == null || isNaN(n)) return '—';
  return Number(n).toFixed(1) + '%';
}

function fmtN(n) {
  if (n == null || isNaN(n)) return '—';
  return Number(n).toLocaleString();
}

function StatusBadge({ status }) {
  const colors = {
    Sent: { bg: '#e6f9f0', color: '#1a8a4a' },
    Sending: { bg: '#e8f0fe', color: '#00395C' },
    Draft: { bg: '#f5f5f5', color: '#5f6368' },
    Scheduled: { bg: '#fff8e6', color: '#b06a00' },
    Active: { bg: '#e6f9f0', color: '#1a8a4a' },
    Deactivated: { bg: '#fef2f2', color: '#b91c1c' },
    NotStarted: { bg: '#f5f5f5', color: '#5f6368' },
  };
  const s = colors[status] || colors.Draft;
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: s.bg, color: s.color, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {status || 'Unknown'}
    </span>
  );
}

const NEON = '/.netlify/functions/neon';

function ConversionsPanel({ campaign, dateSent }) {
  const [state, setState] = useState('idle'); // idle | loading | done | error
  const [orders, setOrders] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  function load() {
    setState('loading');
    fetch(`${PROXY}/campaign-conversions?id=${campaign.id}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) throw new Error(data.error);
        const emails = data.clickers.map(c => c.email);
        const sent = data.dateSent;
        return fetch(NEON, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...sessionHeaders() },
          body: JSON.stringify({ query: 'email_campaign_conversions', params: { emails, dateSent: sent, windowDays: 7 } }),
        }).then(r => r.json());
      })
      .then(result => {
        if (result.error) throw new Error(result.error);
        setOrders(result.rows || []);
        setState('done');
      })
      .catch(e => { setErrorMsg(e.message); setState('error'); });
  }

  const totalRevenue = orders?.reduce((s, o) => s + parseFloat(o.order_amount || 0), 0) ?? 0;
  const vipOrders = orders?.filter(o => o.vip_status > 0) || [];
  const nonVipOrders = orders?.filter(o => !o.vip_status || o.vip_status <= 0) || [];
  const vipRevenue = vipOrders.reduce((s, o) => s + parseFloat(o.order_amount || 0), 0);
  const nonVipRevenue = nonVipOrders.reduce((s, o) => s + parseFloat(o.order_amount || 0), 0);

  if (state === 'idle') {
    return (
      <div style={{ textAlign: 'center', paddingTop: 40 }}>
        <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 14 }}>
          Find orders placed within 7 days by people who clicked this campaign.
        </div>
        <button onClick={load} style={{ background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          Load Conversions
        </button>
      </div>
    );
  }

  if (state === 'loading') {
    return <div style={{ fontSize: 12, color: 'var(--gray-500)', paddingTop: 24 }}>Fetching click &amp; order data...</div>;
  }

  if (state === 'error') {
    return <div style={{ fontSize: 12, color: 'var(--red)', paddingTop: 24 }}>Error: {errorMsg}</div>;
  }

  return (
    <div>
      {/* Summary bar */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
        <div style={{ background: '#f0f7ff', borderRadius: 8, padding: '10px 12px' }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>Conversions</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--navy)' }}>{orders.length}</div>
          <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>orders within 7 days</div>
        </div>
        <div style={{ background: '#fff8f5', border: '1px solid #f4d0c0', borderRadius: 8, padding: '10px 12px' }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--orange)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>Revenue</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--navy)' }}>${totalRevenue.toFixed(2)}</div>
          <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>from clickers</div>
        </div>
      </div>
      {orders.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
          <div style={{ background: '#fff8e6', borderRadius: 8, padding: '8px 12px' }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#b06a00', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>VIP Members</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--navy)' }}>{vipOrders.length} <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--gray-500)' }}>orders</span></div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)' }}>${vipRevenue.toFixed(2)}</div>
            <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>AOV ${vipOrders.length > 0 ? (vipRevenue / vipOrders.length).toFixed(2) : '0.00'}</div>
          </div>
          <div style={{ background: 'var(--gray-50)', borderRadius: 8, padding: '8px 12px' }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>Non-VIP</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--navy)' }}>{nonVipOrders.length} <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--gray-500)' }}>orders</span></div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)' }}>${nonVipRevenue.toFixed(2)}</div>
            <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>AOV ${nonVipOrders.length > 0 ? (nonVipRevenue / nonVipOrders.length).toFixed(2) : '0.00'}</div>
          </div>
        </div>
      )}

      {orders.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>No matching orders found in Neon within 7 days of send.</div>
      )}

      {orders.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Order</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Customer</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Amount</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o, i) => (
                <tr key={o.id} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)' }}>
                  <td style={{ padding: '6px 8px', color: 'var(--navy)', fontWeight: 600 }}>#{o.bc_order_id || o.id}</td>
                  <td style={{ padding: '6px 8px', color: 'var(--gray-700)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {o.first_name} {o.last_name}
                      {o.vip_status > 0 && (
                        <VipBadge notRenewing={Number(o.vip_not_renewing) > 0} style={{ fontSize: 8, fontWeight: 800, padding: '1px 5px', borderRadius: 8, background: '#fff8e6', color: '#b06a00', letterSpacing: '0.5px' }} />
                      )}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>{o.email}</div>
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: 'var(--navy)' }}>
                    ${parseFloat(o.order_amount || 0).toFixed(2)}
                  </td>
                  <td style={{ padding: '6px 8px', color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>
                    {new Date(o.order_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SummaryDrawer({ campaign, onClose }) {
  const [summary, setSummary] = useState(null);
  const [domains, setDomains] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('stats');

  useEffect(() => {
    Promise.all([
      fetch(`${PROXY}/campaign-reports?id=${campaign.id}`).then(r => r.json()),
      fetch(`${PROXY}/campaign-domains?id=${campaign.id}`).then(r => r.json()).catch(() => null),
    ])
      .then(([reportData, domainData]) => {
        if (reportData.error) throw new Error(reportData.error);
        setSummary(reportData);
        const rows = domainData?.data || (Array.isArray(domainData) ? domainData : null);
        setDomains(rows);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [campaign.id]);

  const gmail = domains?.find(d => d.domainName?.toLowerCase() === 'gmail.com');

  const s = summary;

  const tabs = ['stats', 'conversions'];
  const tabLabels = { stats: 'Stats', conversions: 'Conversions' };

  return (
    <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 380, background: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', zIndex: 200, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>{campaign.name}</div>
          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>{campaign.subject}</div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--gray-500)', lineHeight: 1 }}>×</button>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--gray-200)', padding: '0 20px' }}>
        {tabs.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '8px 12px 7px',
            fontSize: 12, fontWeight: 600,
            color: activeTab === tab ? 'var(--navy)' : 'var(--gray-500)',
            borderBottom: activeTab === tab ? '2px solid var(--navy)' : '2px solid transparent',
            marginBottom: -1,
          }}>
            {tabLabels[tab]}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
        {activeTab === 'stats' && (
          <>
            {loading && <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>Loading summary...</div>}
            {error && <div style={{ fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
            {s && (
              <>
                {/* Date sent */}
                {s.dateSent && (
                  <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 12 }}>
                    Sent: {new Date(s.dateSent).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </div>
                )}

                {/* Revenue highlight */}
                {s.revenue && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14, padding: '10px 12px', background: '#fff8f5', border: '1px solid #f4d0c0', borderRadius: 8 }}>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--orange)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>Revenue</div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--navy)' }}>{s.revenue}</div>
                      <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>{fmtN(s.numOrders)} orders · {fmtPct(s.conversionRate * 100)} conv.</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--orange)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>Assisted Rev.</div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--navy)' }}>{s.assistedRevenue}</div>
                      <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>{fmtN(s.numAssistedOrders)} orders</div>
                    </div>
                  </div>
                )}

                {/* Stats grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {[
                    { label: 'Sent', value: fmtN(s.numTotalSent) },
                    { label: 'Delivered', value: fmtN(s.numDelivered), sub: fmtPct(s.percentageDelivered * 100) },
                    { label: 'Unique Opens', value: fmtN(s.numUniqueOpens) },
                    { label: 'Open Rate', value: fmtPct(s.percentageUniqueOpens * 100), highlight: true },
                    { label: 'Recipients Clicked', value: fmtN(s.numRecipientsClicked) },
                    { label: 'Click Rate', value: fmtPct(s.percentageUsersClicked * 100), highlight: true },
                    { label: 'Click-to-Open', value: fmtPct(s.percentageClicksToOpens * 100) },
                    { label: 'Unsubscribes', value: fmtN(s.numTotalUnsubscribes), sub: fmtPct(s.percentageUnsubscribes * 100) },
                    { label: 'Soft Bounces', value: fmtN(s.numTotalSoftBounces), sub: fmtPct(s.percentageSoftBounces * 100) },
                    { label: 'Hard Bounces', value: fmtN(s.numTotalHardBounces), sub: fmtPct(s.percentageHardBounces * 100) },
                    { label: 'Forwards', value: fmtN(s.numTotalEstimatedForwards) },
                    { label: 'Replies', value: fmtN(s.numTotalReplies) },
                    ...(gmail ? [{ label: 'Gmail Open Rate', value: fmtPct(gmail.percentageOpenRate * 100), sub: `${fmtN(gmail.numOpens)} opens · ${fmtN(gmail.numSends)} sent`, highlight: true }] : []),
                  ].map(({ label, value, sub, highlight }) => (
                    <div key={label} style={{ background: 'var(--gray-50)', borderRadius: 8, padding: '8px 10px' }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>{label}</div>
                      <div style={{ fontSize: 17, fontWeight: 700, color: highlight ? 'var(--orange)' : 'var(--navy)' }}>{value}</div>
                      {sub && <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>{sub}</div>}
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {activeTab === 'conversions' && (
          <ConversionsPanel campaign={campaign} dateSent={s?.dateSent} />
        )}
      </div>
    </div>
  );
}

function EmailPreviewModal({ campaignId, campaignName, onClose }) {
  const [html, setHtml] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${PROXY}/campaigns?id=${campaignId}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) throw new Error(data.error);
        const content = data.htmlContent || data.plainTextContent;
        if (!content) throw new Error('No email content available');
        setHtml(data.htmlContent || `<pre style="font-family:sans-serif;padding:20px">${data.plainTextContent}</pre>`);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [campaignId]);

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 299 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 680, maxWidth: '95vw', height: '85vh', background: '#fff', borderRadius: 12, boxShadow: '0 8px 40px rgba(0,0,0,0.2)', zIndex: 300, display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>{campaignName}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--gray-500)', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          {loading && <div style={{ padding: 20, fontSize: 12, color: 'var(--gray-500)' }}>Loading preview...</div>}
          {error && <div style={{ padding: 20, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
          {html && (
            <iframe
              title="Email Preview"
              srcDoc={html}
              style={{ width: '100%', height: '100%', border: 'none' }}
              sandbox="allow-same-origin"
            />
          )}
        </div>
      </div>
    </>
  );
}

// Match campaigns to automation programs by name pattern
function matchCampaignsToPrograms(programs, campaigns) {
  // Known mappings: program name → campaign name patterns
  const PROGRAM_CAMPAIGN_MAP = {
    'Welcome': ['Welcome'],
    'Welcome Facebook': ['Welcome Facebook'],
    'Post Purchase': ['Post Purchase'],
    'Abandoned Cart': ['Abandon Cart', 'Abandoned Cart'],
    'Abandon Checkout': ['Abandon Checkout'],
    'Post Delivery': ['Post Delivery'],
    'SMS Double Opt In': ['SMS Double Opt', 'SMS Opt'],
    'Re-Engagement Sample': ['Re-Engagement Sample', 'Re-engagement Sample'],
    'Microsoft DataSet03 Re-engagement': ['Microsoft DataSet', 'MDS3'],
    'MDS3 Re-engagement cont.': ['MDS3 Re-engagement'],
    'Abandoned Browse': ['Abandoned Browse', 'Abandon Browse'],
    'Should be VIP': ['Should be VIP'],
    'VIP Re-Engagement Program': ['VIP Re-Engagement', 'VIP - Reengagement'],
    'Double Opt-In': ['Double Opt-In'],
    'VIP Cash Reminder': ['VIP Cash Reminder'],
    'VIP Cash Reminder - Basic': ['VIP Cash Reminder - Basic'],
    'VIP - Reengagement Program Test': ['VIP - Reengagement Program Test'],
    'Re-engagement program - Past Purchasers': ['Re-engagement Past Purchaser'],
  };

  return programs.map(prog => {
    const patterns = PROGRAM_CAMPAIGN_MAP[prog.name] || [prog.name];
    const matched = campaigns.filter(c => {
      const name = c.name || '';
      return patterns.some(pat => name.toLowerCase().startsWith(pat.toLowerCase()) || name.toLowerCase().includes(pat.toLowerCase()));
    });
    // Filter out obvious non-automation campaigns (regular newsletters with dates like "3/10 Tue...")
    const filtered = matched.filter(c => {
      const name = c.name || '';
      // Skip date-prefixed newsletters (e.g. "11/5 VIP Nov Bonus Offer")
      if (/^\d{1,2}\/\d{1,2}\s/.test(name)) return false;
      return true;
    });
    // Sort by name to get series order (e.g. "- 1", "- 2", "- 3")
    filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return { ...prog, emails: filtered };
  });
}

function AutomationsPanel() {
  const [programs, setPrograms] = useState(null);
  const [allCampaigns, setAllCampaigns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('Active');
  const [expanded, setExpanded] = useState(new Set());
  const [previewCampaign, setPreviewCampaign] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch(`${PROXY}/programs`).then(r => r.json()),
      fetch(`${PROXY}/campaigns?select=200&skip=0`).then(r => r.json()),
    ])
      .then(([progData, campData]) => {
        if (progData.error) throw new Error(progData.error);
        const progs = Array.isArray(progData) ? progData : [];
        const camps = Array.isArray(campData) ? campData : campData.campaigns || [];
        // Sort: Active first, then by name
        progs.sort((a, b) => {
          if (a.status === 'Active' && b.status !== 'Active') return -1;
          if (a.status !== 'Active' && b.status === 'Active') return 1;
          return (a.name || '').localeCompare(b.name || '');
        });
        setPrograms(progs);
        setAllCampaigns(camps);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const enriched = programs && allCampaigns ? matchCampaignsToPrograms(programs, allCampaigns) : [];

  const counts = enriched.length ? {
    All: enriched.length,
    Active: enriched.filter(p => p.status === 'Active').length,
    Deactivated: enriched.filter(p => p.status === 'Deactivated').length,
    Draft: enriched.filter(p => p.status === 'Draft').length,
  } : {};

  const filtered = enriched.filter(p => filter === 'All' ? true : p.status === filter);

  function toggleExpand(id) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="card">
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Automations</span>
        {enriched.length > 0 && <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--gray-500)' }}>{counts.Active} active</span>}
      </div>

      {/* Filter tabs */}
      {enriched.length > 0 && (
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--gray-200)', padding: '0 12px' }}>
          {['Active', 'Deactivated', 'Draft', 'All'].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '8px 12px 7px',
              fontSize: 11, fontWeight: 600,
              color: filter === f ? 'var(--navy)' : 'var(--gray-500)',
              borderBottom: filter === f ? '2px solid var(--navy)' : '2px solid transparent',
              marginBottom: -1,
            }}>
              {f} ({counts[f] || 0})
            </button>
          ))}
        </div>
      )}

      {loading && <div style={{ padding: '16px', fontSize: 12, color: 'var(--gray-500)' }}>Loading automations...</div>}
      {error && <div style={{ padding: '16px', fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
      {!loading && !error && filtered.length === 0 && (
        <div style={{ padding: '16px', fontSize: 12, color: 'var(--gray-500)' }}>No {filter !== 'All' ? filter.toLowerCase() + ' ' : ''}automations found.</div>
      )}
      {filtered.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)', width: 28 }}></th>
                <th style={{ textAlign: 'left', padding: '8px 4px', fontWeight: 700, color: 'var(--gray-500)' }}>Automation</th>
                <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Status</th>
                <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Emails</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Created</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, i) => {
                const isExpanded = expanded.has(p.id);
                const hasEmails = p.emails.length > 0;
                return (
                  <React.Fragment key={p.id}>
                    <tr
                      style={{ borderBottom: isExpanded ? 'none' : '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)', cursor: hasEmails ? 'pointer' : 'default' }}
                      onClick={() => hasEmails && toggleExpand(p.id)}
                    >
                      <td style={{ padding: '9px 4px 9px 12px', width: 28, color: 'var(--gray-400)', fontSize: 13 }}>
                        {hasEmails ? (isExpanded ? '▾' : '▸') : ''}
                      </td>
                      <td style={{ padding: '9px 4px', color: 'var(--navy)', fontWeight: 600 }}>{p.name}</td>
                      <td style={{ padding: '9px 8px', textAlign: 'center' }}><StatusBadge status={p.status} /></td>
                      <td style={{ padding: '9px 8px', textAlign: 'center' }}>
                        {hasEmails ? (
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: '#e8f0fe', color: 'var(--navy)' }}>
                            {p.emails.length} email{p.emails.length !== 1 ? 's' : ''}
                          </span>
                        ) : (
                          <span style={{ fontSize: 10, color: 'var(--gray-400)' }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: '9px 12px', textAlign: 'right', color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>
                        {p.dateCreated ? new Date(p.dateCreated).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                      </td>
                    </tr>
                    {isExpanded && p.emails.map((email, ei) => (
                      <tr key={email.id} style={{ background: '#f8fafc', borderBottom: ei === p.emails.length - 1 ? '1px solid var(--gray-200)' : '1px solid var(--gray-100)' }}>
                        <td style={{ padding: '7px 4px 7px 12px' }}></td>
                        <td colSpan={2} style={{ padding: '7px 4px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 12 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-400)', minWidth: 14 }}>{ei + 1}.</span>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--navy)' }}>{email.name}</div>
                              <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 1 }}>{email.subject || '(no subject)'}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '7px 8px', textAlign: 'center' }}>
                          <button
                            onClick={(e) => { e.stopPropagation(); setPreviewCampaign(email); }}
                            style={{ background: 'none', border: '1px solid var(--gray-300)', borderRadius: 4, padding: '3px 10px', fontSize: 10, fontWeight: 600, color: 'var(--orange)', cursor: 'pointer' }}
                          >
                            Preview
                          </button>
                        </td>
                        <td style={{ padding: '7px 12px', textAlign: 'right', color: 'var(--gray-400)', fontSize: 10, whiteSpace: 'nowrap' }}>
                          <StatusBadge status={email.status} />
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {previewCampaign && (
        <EmailPreviewModal
          campaignId={previewCampaign.id}
          campaignName={previewCampaign.name}
          onClose={() => setPreviewCampaign(null)}
        />
      )}
    </div>
  );
}

export default function EmailCampaigns() {
  const [campaigns, setCampaigns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [activeTab, setActiveTab] = useState('campaigns');

  useEffect(() => {
    fetch(`${PROXY}/campaigns?select=50&skip=0`)
      .then(r => r.json())
      .then(data => {
        if (data.error) throw new Error(data.error);
        const all = Array.isArray(data) ? data : data.campaigns || [];
        setCampaigns(all.filter(c => c.status === 'Sent'));
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const topTabs = [
    { id: 'campaigns', label: 'Campaigns' },
    { id: 'automations', label: 'Automations' },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 24 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Email Campaigns</h2>
        <div style={{ display: 'flex', gap: 0 }}>
          {topTabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              background: activeTab === tab.id ? 'var(--navy)' : 'transparent',
              color: activeTab === tab.id ? '#fff' : 'var(--navy)',
              border: '1px solid var(--navy)',
              borderRight: tab.id === 'campaigns' ? 'none' : '1px solid var(--navy)',
              borderRadius: tab.id === 'campaigns' ? '6px 0 0 6px' : '0 6px 6px 0',
              padding: '5px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'campaigns' && (
        <>
          <div className="card">
            <div className="card-header">Recent Campaigns</div>
            {loading && <div style={{ padding: '16px', fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
            {error && <div style={{ padding: '16px', fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
            {!loading && !error && campaigns?.length === 0 && (
              <div style={{ padding: '16px', fontSize: 12, color: 'var(--gray-500)' }}>No campaigns found.</div>
            )}
            {campaigns?.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                      <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Campaign</th>
                      <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Status</th>
                      <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Subject</th>
                      <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Created</th>
                      <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Stats</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map((c, i) => (
                      <tr key={c.id} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)', cursor: 'pointer' }}
                        onClick={() => setSelected(c)}>
                        <td style={{ padding: '9px 12px', color: 'var(--navy)', fontWeight: 600, maxWidth: 220 }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                        </td>
                        <td style={{ padding: '9px 8px', textAlign: 'center' }}><StatusBadge status={c.status} /></td>
                        <td style={{ padding: '9px 8px', color: 'var(--gray-700)', maxWidth: 240 }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.subject || '—'}</div>
                        </td>
                        <td style={{ padding: '9px 8px', textAlign: 'right', color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>
                          {c.dateCreated ? new Date(c.dateCreated).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                        </td>
                        <td style={{ padding: '9px 12px', textAlign: 'center' }}>
                          <span style={{ fontSize: 11, color: 'var(--orange)', fontWeight: 600 }}>View →</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {selected && (
            <>
              <div onClick={() => setSelected(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.2)', zIndex: 199 }} />
              <SummaryDrawer campaign={selected} onClose={() => setSelected(null)} />
            </>
          )}
        </>
      )}

      {activeTab === 'automations' && <AutomationsPanel />}

      <AttributedOrders channel="email" />
    </div>
  );
}
